import React, { useEffect, useState } from "react";
import SideNavLink from "./SideNavLink";
import {
    LayoutDashboard,
    Sparkles,
    DollarSign,
    ShoppingBag,
    Building2,
    Plug,
    UserCog,
    HelpCircle,
    RefreshCw,
    ListChecks,
    ConciergeBell,
    BookOpenText,
    LogOut,
    X,
} from "lucide-react";
import logo from "../../icons/logo.jpeg";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import {
    hostEarningsReset,
    hostPendingEarningsReset,
    hostProfileReset,
    hostPropertiesReset,
    hostPropertyDetailsReset,
    logout,
    setBlurOff,
    setNavOff,
} from "../../store/slices/hostSlices";
import CsvExport from "./CsvExport";
import { deriveOnboardingState, readOnboardingProgress } from "../../utils/onboarding";

const SideNav = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { hostInfo: hostLogin } = useSelector((state) => state.host);
    const { hostSideNav } = useSelector((state) => state);
    const hostDetails = useSelector((state) => state.hostDetails.Info);
    const { properties } = useSelector((state) => state.hostProperties);
    const hostFlags = useSelector((state) => state.hostFlags);

    const [laptopView, setLaptopView] = useState();

    const onClickCross = () => {
        dispatch(setNavOff());
        dispatch(setBlurOff());
    };

    const handleLogout = () => {
        if (window.confirm("Are you sure you want to log out?")) {
            dispatch(hostEarningsReset());
            dispatch(hostPropertiesReset());
            dispatch(hostPropertyDetailsReset());
            dispatch(hostPendingEarningsReset());
            dispatch(hostProfileReset());
            dispatch(logout());
            navigate("/login");
        }
    };

    const handleResize = () => {
        if (window.innerWidth > 1024) {
            setLaptopView(true);
        } else {
            setLaptopView(false);
            dispatch(setNavOff());
            dispatch(setBlurOff());
        }
    };

    useEffect(() => { handleResize(); }, []);

    const pmsSelections = Array.isArray(hostLogin?.propertyManagementSoftware)
        ? hostLogin.propertyManagementSoftware
        : hostLogin?.propertyManagementSoftware
        ? [hostLogin.propertyManagementSoftware]
        : [];
    const isGuesty = pmsSelections.find((s) => s === "guesty");
    const onboardingState = deriveOnboardingState({
        hostInfo: hostLogin,
        hostDetails,
        properties,
        hostFlags,
        localProgress: readOnboardingProgress(hostLogin?._id),
    });
    const showSetupLink = Boolean(hostLogin);
    const showGuidebookFieldsLink = hostLogin?.safeFieldsSetupEnabled === true || hostDetails?.safeFieldsSetupEnabled === true;

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <>
            {(hostSideNav || laptopView) && (
                <div
                    id="navLeft"
                    className="flex-col items-center w-[260px] bg-white border-r border-gray-200 laptop:sticky laptop:flex top-0 tablet:rounded-r-3xl tablet:z-20 tablet:absolute tablet:h-screen mobile:w-[85%] min-h-[650px]"
                >
                    {/* Close button for mobile/tablet */}
                    <div className="absolute top-4 right-4 laptop:hidden">
                        <button
                            onClick={onClickCross}
                            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-all duration-200"
                        >
                            <X size={16} className="text-gray-500" />
                        </button>
                    </div>

                    {/* Logo */}
                    <div className="flex justify-center items-center h-20 w-full px-6 mt-4">
                        <NavLink to="/host" className="w-full max-w-[130px]">
                            <img
                                className="w-full h-auto"
                                src={logo}
                                alt="Bellhopt"
                                onClick={() => { dispatch(setNavOff()); dispatch(setBlurOff()); }}
                            />
                        </NavLink>
                    </div>

                    {/* Mobile Add Property */}
                    <div className="laptop:hidden w-full px-4 mb-4">
                        <NavLink to="/add-property">
                            <button
                                className="w-full bg-gray-900 text-white font-bold px-4 py-3.5 rounded-xl transition-all duration-200 hover:bg-gray-800 text-sm"
                                onClick={() => { dispatch(setNavOff()); dispatch(setBlurOff()); }}
                            >
                                + Add Property
                            </button>
                        </NavLink>
                        <div className="mt-2">
                            <CsvExport className="w-full" />
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex-grow w-full px-3">
                        <nav className="space-y-0.5">
                            {hostLogin?.ota ? (
                                <>
                                    <SideNavLink icon={Sparkles} label="Concierge" link="concierge" />
                                    <SideNavLink icon={ConciergeBell} label="Concierge Requests" link="service-requests" />
                                    <SideNavLink icon={HelpCircle} label="Support" link="support" />
                                </>
                            ) : (
                                <>
                                    <SideNavLink icon={LayoutDashboard} label="Dashboard" link="host" />
                                    {showSetupLink && <SideNavLink icon={ListChecks} label="Setup" link="onboarding" />}
                                    <SideNavLink icon={Sparkles} label="Concierge" link="concierge-subscription" />
                                    {showGuidebookFieldsLink && (
                                        <SideNavLink icon={BookOpenText} label="Guidebook" link="concierge-guidebook-fields" />
                                    )}
                                    <SideNavLink icon={ConciergeBell} label="Concierge Requests" link="service-requests" />
                                    <SideNavLink icon={DollarSign} label="Earnings" link="earnings" />
                                    <SideNavLink icon={ShoppingBag} label="Orders" link="order-list" />
                                    <SideNavLink icon={Building2} label="Properties" link="properties" />
                                    <SideNavLink icon={Plug} label="Integrations" link="integrations" />
                                    <SideNavLink icon={UserCog} label="Settings" link={`settings/${hostLogin._id}`} />
                                    <SideNavLink icon={HelpCircle} label="Support" link="support" />
                                    {isGuesty === "guesty" && (
                                        <SideNavLink icon={RefreshCw} label="Sync Guesty" link="sync-guesty-properties" />
                                    )}
                                </>
                            )}
                        </nav>
                    </div>

                    {/* Logout */}
                    <div className="w-full px-3 pb-5">
                        <div className="border-t border-gray-100 pt-3">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-3.5 py-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all duration-200 group"
                            >
                                <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-red-100 flex items-center justify-center transition-all">
                                    <LogOut size={15} strokeWidth={2} className="group-hover:text-red-500" />
                                </div>
                                <span className="text-[13px] font-medium">Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SideNav;
